import { Chip, Input } from "@heroui/react";
import { X } from "lucide-react";
import { KeyboardEvent, useState } from "react";

interface TagInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export default function TagInput({
  tags,
  onChange,
  placeholder = "Type and press Enter to add",
  disabled = false,
  className,
}: TagInputProps) {
  const [inputValue, setInputValue] = useState("");

  const addTag = () => {
    const trimmedValue = inputValue.trim();

    if (trimmedValue && !tags.includes(trimmedValue)) {
      onChange([...tags, trimmedValue]);
      setInputValue("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    onChange(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag();
    } else if (e.key === "Backspace" && inputValue === "" && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  return (
    <div className={`flex flex-col gap-3 ${className ?? ""}`}>
      <Input
        aria-label={placeholder}
        disabled={disabled}
        placeholder={placeholder}
        type="text"
        value={inputValue}
        variant="secondary"
        onBlur={addTag}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <Chip key={tag} variant="soft">
              {tag}
              {!disabled && (
                <button
                  aria-label={`Remove ${tag}`}
                  className="hover:bg-surface-secondary -me-1 ms-1 cursor-pointer rounded p-0.5"
                  type="button"
                  onClick={() => removeTag(tag)}
                >
                  <X className="size-3" />
                </button>
              )}
            </Chip>
          ))}
        </div>
      )}
    </div>
  );
}
